import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { baseQueryWithProvider } from "@/redux/baseQuery";
import { createSearchParams } from "@/utils";
import { transformNetwork, transformNetworkHistory, transformNetworkItem } from "./helpers";
import {
  GetNetworkResponse,
  GetNetworkParams,
  RawNetwork,
  GetNetworkHistoryResponse,
  GetNetworkHistoryParams,
  GetNetworkItemResponse,
} from "./types";

export const networkApi = createApi({
  reducerPath: "networkApi",
  baseQuery: baseQueryWithProvider("network"),
  endpoints: (builder) => ({
    getNetwork: builder.query<GetNetworkResponse, GetNetworkParams>({
      query: ({ status, ...params }) => {
        const searchParams = createSearchParams(params);
        if (status) {
          status.forEach((s) => searchParams.append("status[]", s));
        }
        return `?${searchParams.toString()}`;
      },
      providesTags: ["Network"],
      transformResponse: transformNetwork,
    }),
    approveInvitation: builder.mutation<RawNetwork, string>({
      query: (id) => ({
        url: `/${id}`,
        method: "PATCH",
        body: { status: "approve" },
      }),
      invalidatesTags: ["Network"],
    }),
    rejectInvitation: builder.mutation<RawNetwork, string>({
      query: (id) => ({
        url: `/${id}`,
        method: "PATCH",
        body: { status: "reject" },
      }),
      invalidatesTags: ["Network"],
    }),
    inviteNetwork: builder.mutation<RawNetwork, { provider_code: string; tier: number }>({
      query: (body) => ({
        url: "",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Network"],
    }),
    removeNetwork: builder.mutation<void, string>({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Network"],
    }),
    updateNetworkTier: builder.mutation<RawNetwork, { id: string; tier: number }>({
      query: ({ id, tier }) => ({
        url: `/${id}`,
        method: "PATCH",
        body: { tier },
      }),
      invalidatesTags: ["Network"],
    }),
    getNetworkHistory: builder.query<GetNetworkHistoryResponse, GetNetworkHistoryParams>({
      query: ({ id, page, pageSize }) => {
        let queryString = `/${id}/history?`;
        if (page) queryString = `${queryString}&page=${page}`;
        if (pageSize) queryString = `${queryString}&page_size=${pageSize}`;
        return queryString;
      },
      transformResponse: transformNetworkHistory,
    }),
    getNetworkItem: builder.query<GetNetworkItemResponse, string>({
      query: (id) => `/${id}`,
      transformResponse: transformNetworkItem,
    }),
    getReceivedInvitations: builder.query<GetNetworkResponse, void>({
      query: () => {
        const searchParams = createSearchParams({});
        searchParams.append("status[]", "pending-received");
        searchParams.append("page_size", "100");
        return `?${searchParams.toString()}`;
      },
      providesTags: ["Network"],
      transformResponse: transformNetwork,
    }),
  }),
  tagTypes: ["Network"],
});

export const {
  useGetNetworkQuery,
  useInviteNetworkMutation,
  useRemoveNetworkMutation,
  useApproveInvitationMutation,
  useRejectInvitationMutation,
  useLazyGetNetworkHistoryQuery,
  useLazyGetNetworkItemQuery,
  useUpdateNetworkTierMutation,
  useGetReceivedInvitationsQuery,
} = networkApi;
